/**
 * Story-card context kind — Phase 7 sibling helper to `item-card-state.ts`.
 *
 * The narrative `kind` discriminator used by `lib/story-cards/types.ts`
 * (`StoryCardContext`) for share-card / story-export presentations. Lives in
 * `@kraahl/contract` so the discriminator selection is canonical — the
 * presentation builder (`resolveStoryContext`) supplies per-kind shape
 * (auction.endAt, preorder.deadline, etc.) but the `kind` itself is picked
 * by this exhaustive switch on `Listing["format"]` + `Listing["type"]`.
 *
 * Output space DIFFERS from `ItemCardStateId` by design:
 *   - story-cards collapses "fixed_price_with_offers" and "offers_only" into
 *     a single `kind='offer'` with a `variant` axis (hybrid / only) — driven
 *     by the share-card layout (one offer slide vs two)
 *   - story-cards has a `scheduled` kind for drop-pending listings; item-card
 *     reuses `stateId='preorder'` for the same lifecycle (item-card has fewer
 *     pill slots — the pre-order CTA absorbs the scheduled-drop UI)
 *
 * The two helpers MUST stay coordinated on the underlying discriminator —
 * extending `LISTING_FORMATS` requires extending BOTH switches (the fitness
 * pin `tests/unit/fitness-story-cards-canonical-source.test.ts` enforces
 * that `lib/story-cards/types.ts` derives its `kind` from this helper, not
 * from raw fields independently).
 *
 * @see packages/contract/src/item-card-state.ts — sibling helper
 * @see lib/story-cards/types.ts — `StoryCardContext` consumers
 * @see .tondocs/listings/DISCRIMINATORS_HYGIENE_IMPACT.md §3.G, §H Phase 7
 */

import type { ListingFormat, ListingType } from "./listing-adt";

/**
 * Canonical narrative `kind` for story-card presentations. The
 * `StoryCardContext` union in `lib/story-cards/types.ts` is indexed by this
 * tag — extending requires updating both this tuple and the union.
 */
export const STORY_CONTEXT_KINDS = [
  "buyNow",
  "auction",
  "preorder",
  "offer",
  "scheduled",
] as const;

export type StoryContextKind = (typeof STORY_CONTEXT_KINDS)[number];

export type StoryContextLifecycle = {
  /** Listing has `scheduled_publish_at` in the future. */
  isScheduledDrop: boolean;
};

function unreachableStoryFormat(x: never): never {
  throw new Error(`unhandled story-context listing format: ${String(x)}`);
}

/**
 * Pure, total function from `(listing-discriminator, lifecycle)` to the
 * narrative `kind` tag. The story-card builder calls this and only this —
 * no parallel branching on raw fields.
 *
 * Precedence:
 *   1. lifecycle.isScheduledDrop → "scheduled" (drop-pending listings)
 *   2. listing.type === "preorder" → "preorder"
 *   3. sale-model dispatch:
 *      - "auction"                                 → "auction"
 *      - "fixed_price_with_offers" / "offers_only" → "offer"
 *      - "fixed_price"                              → "buyNow"
 */
export function deriveStoryContextKind(
  listing: { format: ListingFormat; type: ListingType },
  lifecycle: StoryContextLifecycle,
): StoryContextKind {
  if (lifecycle.isScheduledDrop) return "scheduled";
  if (listing.type === "preorder") return "preorder";

  switch (listing.format) {
    case "auction":
      return "auction";
    case "fixed_price_with_offers":
    case "offers_only":
      return "offer";
    case "fixed_price":
      return "buyNow";
    default:
      return unreachableStoryFormat(listing.format);
  }
}

/**
 * Hybrid vs offers-only split for `kind='offer'`. Kept here as a single SoT
 * so the `lib/story-cards/types.ts` builder doesn't re-encode the
 * format-to-variant map. Hybrid = `fixed_price_with_offers` (has a list
 * price), Only = `offers_only` (no list price).
 */
export function storyOfferVariant(
  format: ListingFormat,
): "hybrid" | "only" | null {
  if (format === "fixed_price_with_offers") return "hybrid";
  if (format === "offers_only") return "only";
  return null;
}
